import {
  Box,
  Checkbox,
  HStack,
  Icon,
  Link,
  Radio,
  RadioGroup,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ReactNode, useEffect, useState } from "react";
import { FiCheckCircle, FiExternalLink, FiXCircle } from "react-icons/fi";
import { Button, ConfirmModal } from "components/ds";
import {
  LunaCardActions,
  LunaCardButton,
  LunaCardMenu,
  LunaMessage,
  REASON_HEADING,
  REASON_PLACEHOLDER,
  UNREACHABLE_TEXT,
  isReasonSendable,
  reasonMaxLength,
  refusalHandling,
} from "services/luna";
import { useLunaChatIsClosed, useLunaStore } from "state/luna";
import { TextBlock } from "./blocks/TextBlock";

// An answered card retires when its message:updated arrives, normally within a
// second. If none has come by then, the push was missed: refetch.
const RETIRE_FALLBACK_MS = 5_000;

interface LunaActionCardProps {
  message: LunaMessage;
  sessionId: string;
  readOnly?: boolean;
}

// A pending input's card: Luna asking this person to confirm, reject, pick or
// send on an agent's behalf. Laid out like the dashboard chat's confirmation
// card (ploy-client ToolConfirmationBlock): the ask on top, a footer with the
// buttons, or the result once retired. A dashboard card is information only.
export const LunaActionCard = ({
  message,
  sessionId,
  readOnly,
}: LunaActionCardProps) => {
  const actions = message.actions as LunaCardActions;
  const content = typeof message.content === "string" ? message.content : "";
  const showControls =
    !readOnly &&
    actions.state === "live" &&
    (actions.buttons.length > 0 || actions.menu !== null);

  return (
    <Box pl="1px" pr="1px" pb="2px">
      <Box
        borderRadius="component.2xl"
        bg="bg.container"
        boxShadow="0 0 1px 0 rgba(0, 0, 0, 0.32), 0 0 2px 0 rgba(0, 0, 0, 0.08), 0 1px 3px 0 rgba(0, 0, 0, 0.08)"
      >
        {showControls ? (
          <CardControls
            key={message.id}
            actions={actions}
            sessionId={sessionId}
            content={content}
          />
        ) : (
          <>
            <CardBody content={content} actions={actions} />
            {actions.state === "retired" && actions.result && (
              <CardFooter>
                <Box />
                <ResultPill result={actions.result} />
              </CardFooter>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

const CardBody = ({
  content,
  actions,
  children,
}: {
  content: string;
  actions: LunaCardActions;
  children?: ReactNode;
}) => (
  <VStack align="stretch" spacing="md" px="lg" pt="lg" pb="xl">
    {content && <TextBlock block={{ type: "text", content }} />}
    {children}
    {actions.links.length > 0 && (
      <HStack spacing="md" wrap="wrap">
        {actions.links.map((link) => (
          <CardLink key={link.url} label={link.label} url={link.url} />
        ))}
      </HStack>
    )}
  </VStack>
);

const CardFooter = ({ children }: { children: ReactNode }) => (
  <HStack
    px="lg"
    py="md"
    borderTop="1px solid"
    borderColor="border.default"
    justify="space-between"
    spacing="md"
  >
    {children}
  </HStack>
);

// The server's words for how the card ended, verbatim. Neutral, since the
// string is never parsed to pick a colour.
const ResultPill = ({ result }: { result: string }) => (
  <Box px="sm" py="2xs" borderRadius="component.sm" bg="neutral.50">
    <Text fontSize="xs" color="text.body.secondary" fontWeight="medium">
      {result}
    </Text>
  </Box>
);

const CardLink = ({ label, url }: { label: string; url: string }) => (
  <Link
    href={url}
    isExternal
    color="p.600"
    fontSize="sm"
    fontWeight="500"
    display="inline-flex"
    alignItems="center"
    gap={1}
    _hover={{ color: "p.700", textDecoration: "underline" }}
  >
    {label}
    <Icon as={FiExternalLink} boxSize={3} />
  </Link>
);

// idle: waiting on a click. answering: a click is in flight. sent: answered,
// waiting for the message:updated that retires the card. finished: refused in
// a way another click can't fix.
type Phase = "idle" | "answering" | "sent" | "finished";

interface CardError {
  text: string;
  showLink: boolean;
}

const CardControls = ({
  actions,
  sessionId,
  content,
}: {
  actions: LunaCardActions;
  sessionId: string;
  content: string;
}) => {
  const answerAction = useLunaStore((state) => state.answerAction);
  const refreshHistory = useLunaStore((state) => state.refreshHistory);
  const requestComposerFocus = useLunaStore(
    (state) => state.requestComposerFocus,
  );
  const isClosed = useLunaChatIsClosed(sessionId);

  const [phase, setPhase] = useState<Phase>("idle");
  const [clickedId, setClickedId] = useState<string | null>(null);
  const [error, setError] = useState<CardError | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  // The Reject whose reason dialog is open. The dialog holds the typed reason
  // and stays open through a refused or failed click, so nothing is retyped.
  const [reasonFor, setReasonFor] = useState<LunaCardButton | null>(null);

  const busy = phase !== "idle";
  const menu = actions.menu;

  // A closed chat's socket gets no pushes, so refetch straight away there.
  useEffect(() => {
    if (phase !== "sent") return;
    const timer = setTimeout(
      () => refreshHistory(sessionId),
      isClosed ? 0 : RETIRE_FALLBACK_MS,
    );
    return () => clearTimeout(timer);
  }, [phase, isClosed, refreshHistory, sessionId]);

  const answer = async (
    button: LunaCardButton,
    extra: { reason?: string; selectedIds?: string[] } = {},
  ) => {
    setPhase("answering");
    setClickedId(button.actionId);
    setError(null);
    try {
      const reply = await answerAction(sessionId, {
        actionId: button.actionId,
        ...extra,
      });
      if ("error" in reply) {
        const handling = refusalHandling(reply.code);
        setError({ text: reply.error, showLink: handling.showLink });
        setPhase(handling.finished ? "finished" : "idle");
        if (handling.finished) setReasonFor(null);
        if (handling.refetch) refreshHistory(sessionId);
        if (handling.focusComposer) requestComposerFocus(sessionId);
        return;
      }
      setReasonFor(null);
      setPhase("sent");
    } catch {
      // The click may not have landed: let the person try again.
      setError({ text: UNREACHABLE_TEXT, showLink: false });
      setPhase("idle");
    }
  };

  // Every Reject asks for a reason, reasonPrompt or not: the server refuses a
  // reject without one (reason_required) on every surface.
  const handleClick = (button: LunaCardButton) => {
    if (button.kind === "reject" || button.reasonPrompt) {
      setError(null);
      setReasonFor(button);
    } else if (button.kind === "send" && menu) {
      answer(button, { selectedIds: selected });
    } else {
      answer(button);
    }
  };

  const errorText = error && (
    <>
      {error.text}
      {error.showLink && actions.links[0] && (
        <>
          {" "}
          <CardLink label={actions.links[0].label} url={actions.links[0].url} />
        </>
      )}
    </>
  );

  return (
    <>
      <CardBody content={content} actions={actions}>
        {menu && (
          <OptionPicker
            menu={menu}
            selected={selected}
            onChange={setSelected}
            isDisabled={busy}
          />
        )}
      </CardBody>

      <Box
        px="lg"
        py="md"
        borderTop="1px solid"
        borderColor="border.default"
      >
        <HStack justify="space-between" spacing="md">
          <Text fontSize="md" color="text.body.primary">
            {menu ? selectionHint(menu, selected.length) : null}
          </Text>
          {actions.buttons.length > 0 && (
            <HStack spacing="sm" wrap="wrap" justify="flex-end">
              {actions.buttons.map((button) => {
                const danger = button.style === "danger";
                return (
                  <Button
                    key={button.actionId}
                    size="sm"
                    variant={danger ? "outline" : "solid"}
                    tone={danger ? "error" : "success"}
                    leadingIcon={
                      button.kind === "reject" ? (
                        <FiXCircle />
                      ) : button.kind === "confirm" ? (
                        <FiCheckCircle />
                      ) : undefined
                    }
                    isDisabled={
                      busy ||
                      (button.kind === "send" && !!menu && selected.length === 0)
                    }
                    isLoading={
                      (phase === "answering" || phase === "sent") &&
                      clickedId === button.actionId
                    }
                    onClick={() => handleClick(button)}
                  >
                    {button.label}
                  </Button>
                );
              })}
            </HStack>
          )}
        </HStack>
        {error && !reasonFor && (
          <Text fontSize="sm" color="error.600" mt="sm">
            {errorText}
          </Text>
        )}
      </Box>

      {reasonFor && (
        <ConfirmModal
          title="Reject this action"
          body={REASON_HEADING}
          notePlaceholder={REASON_PLACEHOLDER}
          noteMaxLength={reasonMaxLength(reasonFor)}
          requireNote
          confirmLabel={reasonFor.label}
          confirmTone="danger"
          isConfirming={phase === "answering"}
          error={errorText}
          onCancel={() => {
            setReasonFor(null);
            setError(null);
          }}
          onConfirm={(note) => {
            if (isReasonSendable(note, reasonMaxLength(reasonFor))) {
              answer(reasonFor, { reason: note.trim() });
            }
          }}
        />
      )}
    </>
  );
};

const selectionHint = (menu: LunaCardMenu, count: number): string => {
  if (count === 0) return "Select an option to continue";
  return menu.multi ? `${count} selected` : "1 selected";
};

// The menu's options as rows to pick from, like the dashboard chat's
// EntityPicker: a radio per row for one choice, a checkbox for several. Each
// row is the control's own label, so it is one named control to click, tab to
// or read out.
const OptionPicker = ({
  menu,
  selected,
  onChange,
  isDisabled,
}: {
  menu: LunaCardMenu;
  selected: string[];
  onChange: (selected: string[]) => void;
  isDisabled: boolean;
}) => {
  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const rowStyle = (isSelected: boolean) => ({
    w: "full",
    spacing: "sm",
    bg: isSelected ? "p.50" : "bg.container",
    borderWidth: "1px",
    borderColor: isSelected ? "p.300" : "border.default",
    borderRadius: "component.md",
    px: "md",
    py: "sm",
    transition: "all 0.15s",
    _hover: isDisabled
      ? {}
      : { borderColor: "p.300", bg: isSelected ? "p.50" : "bg.section" },
    cursor: isDisabled ? "not-allowed" : "pointer",
  });

  const label = (text: string) => (
    <Text as="span" fontSize="sm" color="text.body.primary" noOfLines={1}>
      {text}
    </Text>
  );

  return (
    <Box maxH="280px" overflowY="auto" pr="2xs">
      {menu.multi ? (
        <VStack align="stretch" spacing="sm">
          {menu.options.map((option) => {
            const isSelected = selected.includes(option.value);
            return (
              <Checkbox
                key={option.value}
                isChecked={isSelected}
                isDisabled={isDisabled}
                onChange={() => toggle(option.value)}
                {...rowStyle(isSelected)}
              >
                {label(option.label)}
              </Checkbox>
            );
          })}
        </VStack>
      ) : (
        <RadioGroup
          value={selected[0] ?? ""}
          onChange={(value) => onChange([value])}
          isDisabled={isDisabled}
        >
          <VStack align="stretch" spacing="sm">
            {menu.options.map((option) => (
              <Radio
                key={option.value}
                value={option.value}
                {...rowStyle(selected[0] === option.value)}
              >
                {label(option.label)}
              </Radio>
            ))}
          </VStack>
        </RadioGroup>
      )}
    </Box>
  );
};
